import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@chronosphereio/chrono-ui';
import { accessStorage, FeatureFlagGate, useFeatureFlags, useIsInternalUser } from '@chronosphereio/core';
import { PromQLEditor } from '@chronosphereio/editors/prometheus';
import type { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { InformationOutline as InfoIcon } from 'mdi-material-ui';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAnalytics, useAppMetadataContext, useTimeRange, useVizFrameworkConfig } from '@/plugin-system';
import {
  QueryAssistant,
  QueryAssistantAcceptAndReject,
  QueryAssistantButton,
  QueryAssistantProvider,
  useIsQueryAssistantEnabled,
  useQueryAssistant,
  useQueryAssistantAcceptAndReject,
  useQueryAssistantShortcut,
} from '@/prometheus-plugin/ai';
import { injectDownsamplingStrategy as injectDownsamplingStrategy } from '@/prometheus-plugin/utils';
import { DEFAULT_PROM_URL } from '../../model';
import { PrometheusOptionsEditor } from './PrometheusOptionsEditor';
import { PromQLVariableSuggestion } from './PrometheusVariableSuggestion';
import { PromQLBuilder } from './promql-builder';
import { PromQLMode, PromQLModeProvider, usePromQLModeContext } from './promql-builder/promql-builder-context';
import { isQuerySupportedByBuilder } from './promql-builder/promql-builder-model';
import { EditorProvider, PrometheusTimeSeriesQueryEditorProps, useEditorState } from './query-editor-model';

/**
 * The options editor component for editing a PrometheusTimeSeriesQuery's spec.
 */
export function PrometheusTimeSeriesQueryEditor(props: PrometheusTimeSeriesQueryEditorProps) {
  // Only show variable suggestion on dashboards, not Metrics Explorer or other pages.
  const ctx = useAppMetadataContext();
  const showVariableSuggestion = ctx?.app === 'native-dashboard';
  return (
    <EditorProvider {...props}>
      <PrometheusQueryAssistantWrapper {...props} showVariableSuggestion={showVariableSuggestion} />
    </EditorProvider>
  );
}

function PrometheusQueryAssistantWrapper(props: PrometheusTimeSeriesQueryEditorProps) {
  const { spec, handleChange } = useEditorState();

  /**
   * Used by query assistant to update the query.
   */
  const onChange = (query: string) => handleChange(query, { shouldTriggerOnChange: true });

  /**
   * Used by query assistant to update and run the query.
   */
  const onRun = () => handleChange(spec.query, { shouldRun: true });

  return (
    <QueryAssistantProvider query={spec.query} onChange={onChange} onRun={onRun}>
      <PrometheusTimeSeriesQueryEditorBase {...props} />
    </QueryAssistantProvider>
  );
}

/**
 * The options editor component for editing a PrometheusTimeSeriesQuery's spec.
 */
function PrometheusTimeSeriesQueryEditorBase(props: PrometheusTimeSeriesQueryEditorProps) {
  const isInternalUser = useIsInternalUser();
  const { showVariableSuggestion = false, onChange, variableOpportunities = [] } = props;
  const { spec, triggerOnChange, handleChange } = useEditorState();
  const { absoluteTimeRange } = useTimeRange();
  const { isAssistantOpen, suggestion } = useQueryAssistant();

  const queryRewrite = useMemo(() => {
    if (!spec.downsampling_strategy || !spec.query) {
      return null;
    }
    return injectDownsamplingStrategy(spec.query, spec.downsampling_strategy);
  }, [spec.downsampling_strategy, spec.query]);

  const handleQueryChange = useCallback(
    (e: string) => {
      handleChange(e);
    },
    [handleChange]
  );

  const config = useVizFrameworkConfig();
  const { enablePromQLQueryBuilder: showBuilder } = useFeatureFlags();

  // When `enablePromQLQueryBuilder` is true, user can switch between editor and builder.
  // When false, user only sees the editor.
  const Builder = <PromQLBuilder value={spec.query} onChange={handleChange} />;

  const isAssistantEnabled = useIsQueryAssistantEnabled();
  const placeholderText = isAssistantEnabled
    ? 'Enter a PromQL query, or Cmd+I to edit with AI'
    : 'Enter a PromQL query';

  const editorRef = useRef<ReactCodeMirrorRef>(null);

  // When the assistant is closed, re-focus on the editor.
  useEffect(() => {
    if (!isAssistantOpen) {
      editorRef.current?.view?.focus();
    }
  }, [isAssistantOpen]);

  const Editor = (
    <PromQLEditor
      ref={editorRef}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      disableBorder
      placeholder={placeholderText}
      data-testid="promQLEditor"
      queryApiEndpoint={DEFAULT_PROM_URL}
      value={spec.query}
      previousValue={isAssistantOpen && suggestion ? suggestion.metadata?.existingQuery : undefined}
      onChange={handleQueryChange}
      onRun={triggerOnChange}
      onBlur={triggerOnChange}
      timeRange={absoluteTimeRange}
      enableBuiltInVariables={config?.enableBuiltInVariables}
    />
  );

  // We use accessStorage because we don't want to trigger a re-render when we update the stored
  // value, as the mode should be owned by the PromQLModeProvider.
  const { getItem: getLastUsedMode, setItem: setLastUsedMode } = accessStorage<PromQLMode>(
    window.localStorage,
    'last-used-prom-query-editor-mode'
  );

  const lastUsedMode = getLastUsedMode();
  let initialMode: PromQLMode = 'code';
  if (showBuilder) {
    if (isInternalUser && spec.query === '') {
      initialMode = lastUsedMode ?? 'builder';
    }
  }

  return (
    <Stack spacing={0.5}>
      <PromQLModeProvider initialMode={initialMode} setLastUsedMode={setLastUsedMode}>
        <PromQLEditorAndBuilder Editor={Editor} Builder={Builder} />
      </PromQLModeProvider>

      <FeatureFlagGate condition={() => config?.enableExperimentalPrometheusDownsampling === true}>
        {queryRewrite && spec.downsampling_strategy !== 'LTTB' && (
          <Box sx={{ opacity: 0.7, fontSize: (t) => t.typography.body2 }}>
            <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
              <Tooltip Content="The original query has been rewritten to apply the downsample strategy. This can be disabled by setting the downsample strategy to 'Off'">
                <InfoIcon fontSize="inherit" color="secondary" />
              </Tooltip>
              <span>Resolved PromQL: </span>{' '}
              <PromQLEditor enableLinter={false} readOnly disableBorder value={queryRewrite} />
            </Stack>
          </Box>
        )}
      </FeatureFlagGate>

      {showVariableSuggestion && (
        <PromQLVariableSuggestion
          query={spec.query}
          onUpdateQuery={handleChange}
          variableOpportunities={variableOpportunities}
        />
      )}

      <PrometheusOptionsEditor spec={spec} onChange={onChange} />
    </Stack>
  );
}

function PromQLEditorAndBuilder({ Editor, Builder }: { Editor: ReactNode; Builder: ReactNode }) {
  const { spec, handleChange } = useEditorState();
  const { mode, setMode } = usePromQLModeContext();
  const config = useVizFrameworkConfig();
  const analytics = useAnalytics();

  useEffect(() => {
    analytics?.track('prometheus_query_editor_view', { mode });
    // Only track when mode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const PrometheusQueryEditorToolbar = config?.PrometheusQueryEditorToolbar;

  const { enablePromQLQueryBuilder } = useFeatureFlags();
  const isAssistantEnabled = useIsQueryAssistantEnabled();
  const { isAssistantOpen, suggestion } = useQueryAssistant();
  const { containerRef } = useQueryAssistantShortcut();
  const { onAccept, onReject } = useQueryAssistantAcceptAndReject({
    feature: 'promql_query_generation',
    evalName: 'user_acceptance',
  });

  return (
    <Stack ref={containerRef} border="1px solid" borderColor="divider" borderRadius={1}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        p={1}
        bgcolor="background.paper"
      >
        {enablePromQLQueryBuilder ? (
          <ToggleButtonGroup
            value={mode}
            onChange={(_, mode) => {
              if (mode === 'builder') {
                const isQuerySupported = isQuerySupportedByBuilder(spec.query);
                if (!isQuerySupported) {
                  // Alert the user
                  const confirm = window.confirm(
                    'This query contains features not supported in builder mode. You may lose parts of your query. Would you like to continue?'
                  );
                  if (!confirm) {
                    return;
                  }
                }
              }
              setMode(mode);
            }}
          >
            <ToggleButton sx={{ height: 24, fontSize: 'small' }} value="code">
              Code
            </ToggleButton>
            <ToggleButton sx={{ height: 24, fontSize: 'small', paddingY: 0 }} value="builder">
              Builder
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}
        {PrometheusQueryEditorToolbar && (
          <PrometheusQueryEditorToolbar query={spec.query} onUpdateQuery={handleChange} mode={mode} />
        )}
      </Stack>

      <Stack gap={1} padding={1}>
        {mode === 'code' && (
          <Stack direction="row" alignItems="start" spacing={1}>
            <Box flexGrow={1}>{Editor}</Box>
            {isAssistantEnabled && !isAssistantOpen && <QueryAssistantButton />}
          </Stack>
        )}
        {mode === 'builder' && Builder}

        {isAssistantOpen && suggestion && <QueryAssistantAcceptAndReject onAccept={onAccept} onReject={onReject} />}

        {isAssistantOpen && <QueryAssistant />}
      </Stack>
    </Stack>
  );
}
